import { defineStore } from 'pinia';
import DocumentApi from '@/modules/document/api/document-api.ts';
import { PREVIEW_FILE_TYPES } from '@/modules/common/consts/file.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import { State } from '@/modules/common/enums/state.ts';
import { getAttachmentGroupKey } from '@/modules/document/components/file-preview/document-attachment-preview.util.ts';
import { usePreviewFilesUiStore } from '@/modules/document/stores/preview-files-ui.ts';
import type DocumentFile from '@/modules/document/models/file/document-file.ts';

type PreviewFilesState = {
  documentId: number | null;
  list: DocumentFile[];
  isLoading: boolean;
  error: unknown | null;
  forbidden: boolean;
  controller: AbortController | null;
  lastRevisionFilesState: Record<string, State>;
};

function sortByNewest<T extends { fileId: number }>(files: T[]): T[] {
  return [...files].sort((left, right) => right.fileId - left.fileId);
}

export const usePreviewFilesStore = defineStore(StoreId.PreviewFiles, {
  state: (): PreviewFilesState => ({
    documentId: null,
    list: [],
    isLoading: false,
    error: null,
    forbidden: false,
    controller: null,
    lastRevisionFilesState: {},
  }),
  getters: {
    isReady(state) {
      return !state.isLoading && !state.error;
    },
    lastRevisionList(state) {
      return sortByNewest(state.list.filter((file) => file.isLast));
    },
    oldRevisionList(state) {
      return state.list.filter((file) => !file.isLast);
    },
    getOldRevisionListForAttachment() {
      return (attachmentFile: Pick<DocumentFile, 'name'>) => {
        const groupKey = getAttachmentGroupKey(attachmentFile);

        return this.list
          .filter((file) => !file.isLast)
          .filter((file) => getAttachmentGroupKey(file) === groupKey)
          .sort((a, b) => b.revision - a.revision);
      };
    },
    previewLastRevisionList(state) {
      return sortByNewest(state.list.filter((file) => file.isLast && PREVIEW_FILE_TYPES.includes(file.type)));
    },
  },
  actions: {
    async fetch(request: number) {
      this.controller?.abort();
      this.controller = new AbortController();

      this.documentId = request;
      this.list = [];
      this.error = null;
      this.forbidden = false;
      this.lastRevisionFilesState = {};
      usePreviewFilesUiStore().reset();
      this.isLoading = true;

      try {
        this.list = await DocumentApi.getFiles(request, this.controller.signal);
      } catch (error) {
        if (ErrorUtil.isForbidden(error)) {
          this.forbidden = true;
        } else if (ErrorUtil.isUnexpected(error)) {
          this.error = error;
        }
      } finally {
        this.isLoading = false;
        this.controller = null;
      }
    },
    updateLastRevisionFilesState({ fileId, state }: { fileId: string; state: State }) {
      this.lastRevisionFilesState = {
        ...this.lastRevisionFilesState,
        [fileId]: state,
      };
    },
  },
});
